import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { COLLECTIONS } from "@/data/siteData";

const TYPE_LABELS = {
  hybrid: "Hybrid Flooring",
  laminate: "Laminate Flooring",
  timber: "Engineered Timber",
};

const FILTER_TABS = [
  { key: "all", label: "All Collections" },
  { key: "hybrid", label: "Hybrid" },
  { key: "laminate", label: "Laminate" },
  { key: "timber", label: "Engineered Timber" },
];

export default function CollectionsPage() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const collections = COLLECTIONS;

  // Close lightbox on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (!lightbox) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") navigateLightbox(1);
      if (e.key === "ArrowLeft") navigateLightbox(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  const filtered = activeFilter === "all"
    ? collections
    : collections.filter(c => c.type === activeFilter);

  // Group by type + collection name
  const grouped = [];
  const groupMap = {};
  filtered.forEach(item => {
    const groupKey = `${item.type}|${item.collection}`;
    if (!groupMap[groupKey]) {
      groupMap[groupKey] = { label: `${TYPE_LABELS[item.type] || item.type} — ${item.collection} Collection`, items: [], type: item.type, collection: item.collection };
      grouped.push(groupMap[groupKey]);
    }
    groupMap[groupKey].items.push(item);
  });

  const openLightbox = (product, groupItems) => {
    const idx = groupItems.findIndex(p => p.id === product.id);
    setLightbox({ product, allInGroup: groupItems, index: idx });
  };

  const navigateLightbox = (dir) => {
    if (!lightbox) return;
    const { allInGroup, index } = lightbox;
    const newIdx = (index + dir + allInGroup.length) % allInGroup.length;
    setLightbox({ product: allInGroup[newIdx], allInGroup, index: newIdx });
  };

  return (
    <main data-testid="collections-page" className="pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Our Range</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            Wood Collections
          </h1>
          <p className="font-sans text-base text-[#A3A3A3] max-w-2xl mx-auto">
            Browse our premium range of hybrid, laminate, and engineered timber flooring. Tap any product to view it in detail.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-14" data-testid="collection-filters">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.key}
                data-testid={`collection-filter-${tab.key}`}
                onClick={() => setActiveFilter(tab.key)}
                className={`px-6 py-2.5 text-sm font-sans uppercase tracking-wider transition-all duration-200 ${
                  activeFilter === tab.key
                    ? "bg-[#D4AF37] text-black font-semibold"
                    : "border border-white/10 text-[#A3A3A3] hover:border-[#D4AF37]/40 hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Collection Groups */}
          {grouped.map((group) => (
            <div key={`${group.type}-${group.collection}`} className="mb-14">
              <div className="flex items-center gap-4 mb-8">
                <div className="gold-line flex-1" />
                <h2 className="font-serif text-lg sm:text-xl text-white whitespace-nowrap text-center">
                  {group.label}
                  <span className="block text-xs font-sans text-[#D4AF37]/70 mt-1 tracking-wider">
                    {group.items[0]?.thickness}
                  </span>
                </h2>
                <div className="gold-line flex-1" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {group.items.map(product => (
                  <button
                    key={product.id}
                    data-testid={`product-${product.id}`}
                    onClick={() => openLightbox(product, group.items)}
                    className="group bg-[#121212] border border-white/5 overflow-hidden transition-all duration-300 hover:border-[#D4AF37]/40 hover:shadow-[0_4px_20px_rgba(212,175,55,0.08)] text-left cursor-pointer"
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-[#050505]/0 group-hover:bg-[#050505]/40 transition-colors duration-300 flex items-center justify-center">
                        <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-serif text-sm text-white group-hover:text-[#D4AF37] transition-colors leading-tight">
                        {product.name}
                      </h3>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="font-sans text-xs text-[#A3A3A3]">{product.thickness}</span>
                        <span className="font-sans text-[10px] text-[#D4AF37]/70">{product.code}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* CTA */}
          <div className="text-center mt-8 pt-10 border-t border-white/5">
            <p className="font-sans text-[#A3A3A3] text-sm mb-6">
              Can't find what you're looking for? We have many more options available. Contact us for our full product range.
            </p>
            <Link
              to="/contact"
              data-testid="collections-cta"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-10 py-4 text-sm uppercase tracking-wider hover:bg-[#F0C74A] transition-colors"
            >
              Request a Sample
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightbox && (
        <div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors z-10"
            >
              <X className="w-7 h-7" />
            </button>

            {/* Navigation */}
            {lightbox.allInGroup.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  onClick={() => navigateLightbox(-1)}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-14 text-white/50 hover:text-[#D4AF37] transition-colors hidden md:block"
                >
                  <ChevronLeft className="w-10 h-10" />
                </button>
                <button
                  data-testid="lightbox-next"
                  onClick={() => navigateLightbox(1)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-14 text-white/50 hover:text-[#D4AF37] transition-colors hidden md:block"
                >
                  <ChevronRight className="w-10 h-10" />
                </button>
              </>
            )}

            {/* Image */}
            <div className="border border-[#D4AF37]/20 overflow-hidden">
              <img
                data-testid="lightbox-image"
                src={lightbox.product.image}
                alt={lightbox.product.name}
                className="w-full aspect-square object-cover"
              />
            </div>

            {/* Product Info */}
            <div className="mt-4 flex items-center justify-between">
              <div>
                <h3 className="font-serif text-2xl text-white">{lightbox.product.name}</h3>
                <p className="font-sans text-sm text-[#A3A3A3] mt-1">
                  {lightbox.product.collection} Collection
                </p>
              </div>
              <div className="text-right">
                <p className="font-sans text-sm text-[#D4AF37]">{lightbox.product.thickness}</p>
                <p className="font-sans text-xs text-[#A3A3A3] mt-1">Code: {lightbox.product.code}</p>
              </div>
            </div>

            {/* Counter & Mobile Nav */}
            <div className="mt-4 flex items-center justify-between">
              <p className="font-sans text-xs text-[#A3A3A3]">
                {lightbox.index + 1} of {lightbox.allInGroup.length}
              </p>
              <div className="flex gap-3 md:hidden">
                <button
                  data-testid="lightbox-prev-mobile"
                  onClick={() => navigateLightbox(-1)}
                  className="border border-white/10 p-2 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  data-testid="lightbox-next-mobile"
                  onClick={() => navigateLightbox(1)}
                  className="border border-white/10 p-2 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <Link
                to="/contact"
                data-testid="lightbox-enquire"
                onClick={() => setLightbox(null)}
                className="text-[#D4AF37] text-sm font-sans uppercase tracking-wider hover:text-[#F0C74A] transition-colors"
              >
                Enquire About This Product
              </Link>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
